import { useRef, useMemo, useEffect } from 'react'
import { Box, HStack, Text, Button } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { FiEdit3, FiMousePointer } from 'react-icons/fi'
import type { Map as MapLibreMap } from 'maplibre-gl'
import { TurfMap } from './TurfMap'
import { useTerraDraw } from '../../hooks/useTerraDraw'
import { getTurfColor } from '../../lib/mapConfig'
import type { ContactLocation } from '../../types/contactList'
import type { HouseholdMarker } from '../../types/turfMap'
import type { WorkplaceMarker, OverlayMode } from '../../types/workplaceOverlay'

// Re-export DrawnTurf from the hook for backwards compatibility
export type { DrawnTurf } from '../../hooks/useTerraDraw'
import type { DrawnTurf } from '../../hooks/useTerraDraw'

export interface MultiTurfDrawingMapProps {
  contacts: ContactLocation[]
  onTurfsChange: (turfs: DrawnTurf[]) => void
  onHouseholdContactClick?: (contactId: string) => void
  /** Workplace markers — when present, rendered alongside or instead of households */
  workplaces?: WorkplaceMarker[]
  /** Household markers override — for hybrid mode (only contacts without employment) */
  householdOverride?: HouseholdMarker[]
  onWorkplaceContactClick?: (contactId: string) => void
  onWorksiteClick?: (businessId: string) => void
  height?: string
  /** Callback receiving terra-draw methods for parent/sibling components (replaces window API) */
  onMethodsReady?: (methods: TurfMapMethods) => void
  /** Label prefix for default turf names (e.g. "Downtown" → "Downtown-1") */
  turfMapLabel?: string
  /** Controls which contact location (home/work/hybrid) is used for point-in-polygon checks */
  overlayMode?: OverlayMode
  /** Suppress the built-in desktop Draw/Cancel toolbar (mobile owns its own FAB stack). */
  hideToolbar?: boolean
  /** Enlarge terra-draw hit targets for touch (mobile). */
  touch?: boolean
  /** Reactive drawing-mode state pushed to the parent (for external FAB controls). */
  onDrawingModeChange?: (drawing: boolean) => void
}

export interface TurfMapMethods {
  deleteTurf: (id: string) => void
  updateTurfLabel: (id: string, label: string) => void
  loadPreviewTurfs: (turfs: import('../../types/turf').AutoTurfPreviewTurf[]) => void
  flyToTurf: (id: string) => void
  startDrawing: () => void
  stopDrawing: () => void
}

export function MultiTurfDrawingMap({
  contacts,
  onTurfsChange,
  onHouseholdContactClick,
  workplaces,
  householdOverride,
  onWorkplaceContactClick,
  onWorksiteClick,
  height = '600px',
  onMethodsReady,
  turfMapLabel,
  overlayMode,
  hideToolbar = false,
  touch = false,
  onDrawingModeChange,
}: MultiTurfDrawingMapProps) {
  const mapRef = useRef<MapLibreMap | null>(null)
  const methodsReadyRef = useRef(false)

  const {
    isDrawingMode,
    startDrawing,
    stopDrawing,
    deleteTurf,
    updateTurfLabel,
    flyToTurf,
    loadExistingPolygons,
    handleMapLoad: hookMapLoad,
  } = useTerraDraw({
    mapRef,
    contacts,
    onTurfsChange,
    editOnly: false,
    turfMapLabel,
    overlayMode,
    touch,
  })

  useEffect(() => {
    onDrawingModeChange?.(isDrawingMode)
  }, [isDrawingMode, onDrawingModeChange])

  // Group contacts into households (client-side grouping)
  const households: HouseholdMarker[] = useMemo(() => {
    const householdMap = new Map<string, HouseholdMarker>()

    contacts.forEach(contact => {
      if (!contact.streetAddress || !contact.zip) return

      const key = `${contact.streetAddress.trim().toUpperCase()}|${contact.zip}`

      if (!householdMap.has(key)) {
        householdMap.set(key, {
          householdKey: key,
          longitude: contact.longitude,
          latitude: contact.latitude,
          streetAddress: contact.streetAddress,
          city: '',
          state: null,
          zip: contact.zip,
          contactCount: 0,
          visitedCount: 0,
          needsRevisitCount: 0,
          contacts: [],
        })
      }

      const household = householdMap.get(key)!
      household.contactCount += 1
      household.contacts.push({
        contactId: contact.id,
        contactName: `${contact.firstName} ${contact.lastName}`,
        walkOrder: 0,
        isVisited: false,
        contactStatus: 0,
        turfId: '',
        turfIndex: 0,
      })
    })

    return Array.from(householdMap.values())
  }, [contacts])

  // Wrap map load to capture ref and notify hook
  const handleMapLoad = (map: MapLibreMap) => {
    mapRef.current = map
    hookMapLoad(map)

    // Notify parent of available methods (once)
    if (!methodsReadyRef.current && onMethodsReady) {
      methodsReadyRef.current = true
      onMethodsReady({
        deleteTurf,
        updateTurfLabel,
        loadPreviewTurfs: (previewTurfs) => {
          const polygons = previewTurfs.map(preview => {
            const geometry = JSON.parse(preview.boundaryGeoJson)
            return {
              id: `preview-${preview.turfIndex}`,
              geometry,
              label: preview.label,
              color: getTurfColor(preview.turfIndex),
              index: preview.turfIndex,
              contactIds: preview.contactIds,
              householdCount: preview.householdCount,
            }
          })
          loadExistingPolygons(polygons)
        },
        flyToTurf,
        startDrawing,
        stopDrawing,
      })
    }
  }

  // Also maintain window API for backwards compat during transition
  // TurfCutterPage still uses it until fully migrated
  if (typeof window !== 'undefined') {
    ;(window as any).__multiTurfMapMethods = {
      deleteTurf,
      updateTurfLabel,
      loadPreviewTurfs: (previewTurfs: import('../../types/turf').AutoTurfPreviewTurf[]) => {
        const polygons = previewTurfs.map(preview => {
          const geometry = JSON.parse(preview.boundaryGeoJson)
          return {
            id: `preview-${preview.turfIndex}`,
            geometry,
            label: preview.label,
            color: getTurfColor(preview.turfIndex),
            index: preview.turfIndex,
            contactIds: preview.contactIds,
            householdCount: preview.householdCount,
          }
        })
        loadExistingPolygons(polygons)
      },
      flyToTurf,
      startDrawing,
      stopDrawing,
    }
  }

  return (
    <div style={{ position: 'relative', height }}>
      <TurfMap
        onMapLoad={handleMapLoad}
        households={householdOverride ?? households}
        workplaces={workplaces}
        onHouseholdContactClick={onHouseholdContactClick}
        onWorkplaceContactClick={onWorkplaceContactClick}
        onWorksiteClick={onWorksiteClick}
        disableInteractions={isDrawingMode}
        height={height}
        fitToBounds={true}
      />

      {/* Drawing Toolbar */}
      {!hideToolbar && (
        <Box
          position="absolute"
          bottom={4}
          right={4}
          bg="surface"
          px={3}
          py={2}
          borderRadius="md"
          shadow="lg"
          zIndex={10}
        >
          <HStack gap={3}>
            {isDrawingMode ? (
              <>
                <Tooltip content="Cancel drawing (or right-click)">
                  <Button size="sm" colorPalette="gray" onClick={stopDrawing}><FiMousePointer />Cancel
                                      </Button>
                </Tooltip>
                <HStack gap={2}>
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg="green.400"
                    animation="pulse 2s infinite"
                  />
                  <Text fontSize="xs" color="gray.600">
                    Drawing... right-click to cancel
                  </Text>
                </HStack>
              </>
            ) : (
              <>
                <Tooltip content="Start drawing a turf polygon">
                  <Button size="sm" colorPalette="brand" onClick={startDrawing}><FiEdit3 />Draw Turf
                                      </Button>
                </Tooltip>
                <Text fontSize="xs" color="gray.500">
                  Click a polygon to select &amp; edit vertices
                </Text>
              </>
            )}
          </HStack>
        </Box>
      )}
    </div>
  );
}
